import React, { useState } from 'react';

import dayjs from 'dayjs';

import { InboxOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Select,
  message,
  Upload,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { RAPPI_PROVIDER } from 'constants/rappi';
import { postFormFile, sendPutFile } from 'services/files.service';
interface UploadFilesProps {
	isSuccessUpload?: ()=>void;
}
interface UploadFormValues {
  typeFile: string;
  month: number;
  year: number;
  documentName: string;
  userType?: string;
}

const { Option } = Select;

const formItemLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 30 },
};

const normFile = (e: UploadFile[] | { fileList: UploadFile[] }): UploadFile[] | undefined => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

const UploadForm: React.FC<UploadFilesProps> = ({
isSuccessUpload
}) => {

  const [form] = Form.useForm();

  const currentYear = dayjs().year();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const onReset = () => {
    form.resetFields();
  };

  const updateFile = async (url:string) => {
    try {
      setIsLoading(true);
      const resp = await sendPutFile(fileList[0], url);
      if(resp){
        setFileList([]);
        message.success('Carga de archivo exitosa.');
        onReset();
        isSuccessUpload && isSuccessUpload();
      }
      setIsLoading(false);
    } catch (error) {
      message.error('Carga de archivo fallida, intente más tarde.');
      setIsLoading(false);
    }
  }

  const onFinish = async (values: UploadFormValues) => {
    try {
      const payloadCertification = {
        provider: RAPPI_PROVIDER,
        type: values.typeFile,
        month: Number(values.month),
        year: Number(values.year),
        file_name: `${values.documentName}${fileList[0].name.match(/(.*)(\..*)/)[2]}`,
        user_type: values.userType,
      }
      const payloadOther = {
        provider: RAPPI_PROVIDER,
        type: values.typeFile,
        file_name: `${values.documentName}${fileList[0].name.match(/(.*)(\..*)/)[2]}`,
      }

      const resp = await postFormFile(values.typeFile === 'other' ? JSON.stringify(payloadOther) : JSON.stringify(payloadCertification))

      if(resp){
        updateFile(resp.data.Body.responseCall.data.data.UserMsj)
      } else {
        message.error('Carga de archivo fallida, intente más tarde.');
      }
    } catch (error) {
      message.error('Carga de archivo fallida, intente más tarde.');
    }
  };
  
  const props: UploadProps = {
    onRemove: file => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: file => {
      setFileList([...fileList, file]);
      return false;
    },
    fileList,
    maxCount: 1,
  };
  
	return (
    <Form
      form={form}
      name="validate_other"
      {...formItemLayout}
      onFinish={onFinish}
    >
      <Form.Item name="documentName" label="Nombre del CSV" rules={[{ required: true, message: 'El nombre del documento es requerido' }]}>
        <Input />
      </Form.Item>
      <Form.Item
        name="typeFile"
        label="Tipo de archivo"
        hasFeedback
        rules={[{ required: true, message: 'Debes seleccionar el tipo de archivo' }]}
      >
        <Select placeholder="Selecciona el tipo de archivo">
          <Option value="certification">Certificación</Option>
          <Option value="other">Otro</Option>
        </Select>
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.typeFile !== currentValues.typeFile}
      >
        {({ getFieldValue }) =>
          getFieldValue('typeFile') === 'certification' ? (
            <>
             <Form.Item
                name="month"
                label="Mes de certificación"
                hasFeedback
                rules={[{ required: true, message: 'Debes seleccionar el mes a cargar' }]}
            >
              <Select placeholder="Selecciona el mes de certificación">
                {[...Array(12)].map((_, index) => (
                  <option key={index + 1} value={(index + 1).toString()}>
                    {(index + 1).toString()}
                  </option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="year"
              label="Año de certificación"
              hasFeedback
              rules={[{ required: true, message: 'Debes seleccionar el año a cargar' }]}
            >
              <Select placeholder="Selecciona el año de certificación">
                {[...Array(5)].map((_, index) => {
                  const year = currentYear - index;
                  return (
                    <Option key={year} value={year.toString()}>
                      {year.toString()}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>

              <Form.Item
                name="userType"
                label="Tipo de usuario"
                hasFeedback
                rules={[{ required: true, message: 'Debes seleccionar el tipo de usuario a cargar' }]}
              >
                <Select placeholder="Selecciona el tipo de usuario">
                  <Option value="RTs">RTs</Option>
                  <Option value="Aliados">Aliados</Option>
                </Select>
              </Form.Item>
            </>
          ) : null
        }
      </Form.Item>

      <Form.Item>
        <Form.Item name="file" valuePropName="fileList" getValueFromEvent={normFile} noStyle>
          <Upload.Dragger {...props}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Cargar un archivo</p>
            <p className="ant-upload-hint">Selecciona o arrastra el archivo aquí</p>
          </Upload.Dragger>
        </Form.Item>
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 20 }}>
        <Button 
          type="primary" 
          htmlType="submit"
          loading={isLoading}
          disabled={fileList.length === 0}
        >
          Enviar
        </Button>
      </Form.Item>
    </Form>
	);
};

export default React.memo(UploadForm);
